import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Building2, Phone, Mail, Pencil, DollarSign, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { inventoryApi } from "./inventoryApi";
import { formatCurrency } from "./inventoryConstants";

const empty = { name: "", contact: "", phone: "", email: "", address: "", notes: "" };

export const VendorsSection = ({ currentUser, canManage }) => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Unknown";

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try { setVendors(await inventoryApi.getVendors()); } catch { toast.error("Failed to load vendors"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const openAdd = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (v) => { setForm({ ...empty, ...v }); setEditId(v.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Vendor name required"); return; }
    try {
      if (editId) { await inventoryApi.updateVendor(editId, { ...form, updated_by: userName }); toast.success("Vendor updated"); }
      else { await inventoryApi.createVendor({ ...form, created_by: userName }); toast.success("Vendor added"); }
      setOpen(false); fetchVendors();
    } catch { toast.error("Failed to save vendor"); }
  };

  return (
    <div className="space-y-4" data-testid="vendors-section">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-500">{vendors.length} vendors</span>
        {canManage && <Button onClick={openAdd} data-testid="add-vendor-btn"><Plus className="w-4 h-4 mr-1" /> Add Vendor</Button>}
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {vendors.map((v) => (
            <div key={v.id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`vendor-card-${v.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center"><Building2 className="w-5 h-5 text-slate-500" /></div>
                  <div>
                    <div className="font-semibold text-slate-900">{v.name}</div>
                    {v.contact && <div className="text-xs text-slate-500">{v.contact}</div>}
                  </div>
                </div>
                {canManage && <button onClick={() => openEdit(v)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Pencil className="w-4 h-4" /></button>}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-slate-600">
                {v.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{v.phone}</span>}
                {v.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{v.email}</span>}
              </div>
              <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                <div className="flex-1 text-center"><div className="text-sm font-semibold text-slate-900 inline-flex items-center gap-0.5"><DollarSign className="w-3 h-3" />{formatCurrency(v.total_spend).replace("$", "")}</div><div className="text-[10px] text-slate-400">Total Spend</div></div>
                <div className="flex-1 text-center"><div className="text-sm font-semibold text-amber-600">{v.open_pos}</div><div className="text-[10px] text-slate-400">Open POs</div></div>
                <div className="flex-1 text-center"><div className="text-sm font-semibold text-red-600">{v.open_cores}</div><div className="text-[10px] text-slate-400">Open Cores</div></div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="vendor-dialog">
          <DialogTitle className="sr-only">{editId ? "Edit Vendor" : "Add Vendor"}</DialogTitle>
          <DialogDescription className="sr-only">Vendor record form</DialogDescription>
          <h3 className="text-lg font-semibold mb-3">{editId ? "Edit Vendor" : "Add Vendor"}</h3>
          <div className="space-y-3">
            <div><Label className="text-xs text-slate-500">Vendor Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vendor-name-input" /></div>
            <div><Label className="text-xs text-slate-500">Contact</Label><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-500">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label className="text-xs text-slate-500">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            </div>
            <div><Label className="text-xs text-slate-500">Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label className="text-xs text-slate-500">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div className="flex gap-2"><Button variant="outline" className="flex-1" onClick={() => setOpen(false)}>Cancel</Button><Button className="flex-1" onClick={save} data-testid="vendor-save">Save</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
